import { useState } from 'react'
import { FileUp, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { inlineInput } from '@/lib/form-styles'
import { importProjectLv, inspectProjectLv, type LvZeichensatz, type ProjectLvHead, type ProjectLvPreview } from '../api/project-lv.api'
import { geldText } from '@symphenia/shared-types'
import { LV_FELD, LV_PHASE, lvWert } from './lv-labels'

/** Preise schreibt `geldText` (1.234,50), alles andere `lvWert`. */
const wert = (feld: string, v: unknown) => (feld === 'unitPrice' ? geldText(v as number) || '—' : lvWert(v))

function Baum({ gruppen, tiefe = 0 }: { gruppen: ProjectLvPreview['groups']; tiefe?: number }) {
  return (
    <>
      {gruppen.map((g) => (
        <div key={g.oz}>
          <div className="flex justify-between gap-2 text-sm" style={{ paddingLeft: `${tiefe}rem` }}>
            <span className="truncate">
              <span className="tabular-nums text-muted-foreground">{g.oz}</span> {g.name}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">{g.items}</span>
          </div>
          <Baum gruppen={g.groups} tiefe={tiefe + 1} />
        </div>
      ))}
    </>
  )
}

/**
 * AP-35 Detailblatt 12 4.2 / 6. — **LV importieren**: Datei → Erkennen → kurze
 * Vorschau (Kopf, Gliederung, Zähler, Einheiten) → **Importieren**. Liegt schon
 * ein LV im Projekt, wird es die **neue Version** (Vorschau mit Abgleich je OZ,
 * nur lesend); „als eigenes LV" legt ein zweites, unabhängiges an. Es entsteht
 * keine Position — kopiert wird danach im Reiter LV.
 */
export function ProjectLvImportDialog({
  projectId,
  aktuelles,
  onClose,
  onDone,
}: {
  projectId: string
  aktuelles: ProjectLvHead | null
  onClose: () => void
  onDone: (lv: ProjectLvHead) => void
}) {
  const [datei, setDatei] = useState<File | null>(null)
  const [zeichensatz, setZeichensatz] = useState<LvZeichensatz | ''>('')
  const [eigenes, setEigenes] = useState(false)
  const [kopf, setKopf] = useState(false)
  const [vorschau, setVorschau] = useState<ProjectLvPreview | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const pruefen = async (d: File, zs: LvZeichensatz | '', alsEigenes: boolean) => {
    setLaeuft(true)
    setFehler(null)
    setVorschau(null)
    try {
      setVorschau(await inspectProjectLv(projectId, d, { encoding: zs || undefined, asNewLv: alsEigenes }))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht lesen.'))
    } finally {
      setLaeuft(false)
    }
  }

  const importieren = async () => {
    if (!datei) return
    setLaeuft(true)
    setFehler(null)
    try {
      onDone(await importProjectLv(projectId, datei, { encoding: zeichensatz || undefined, asNewLv: eigenes, headFields: kopf }))
    } catch (e) {
      setFehler(apiErrorText(e, 'Das LV ließ sich nicht importieren.'))
    } finally {
      setLaeuft(false)
    }
  }

  const v = vorschau
  const falscherZeichensatz = v?.notes.some((n) => n.code === 'LV_ENCODING')
  const alsVersion = !!aktuelles && !eigenes

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-lg border border-border p-6 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-primary">{alsVersion ? `LV Version ${aktuelles!.version + 1} importieren` : 'LV importieren'}</h2>
            <p className="text-xs text-muted-foreground">
              ONLV (ÖNORM A 2063), ÖNORM B 2063 oder GAEB DA XML. Es entsteht keine Position — aus dem LV wird danach kopiert und eingefügt, wo du willst.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        {fehler && <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
            <FileUp className="h-4 w-4" /> {datei ? datei.name : 'Datei wählen …'}
            <input
              type="file"
              className="hidden"
              accept=".onlv,.xml,.zip,.x81,.x83,.x84,.x86,.dtn,.txt"
              onChange={(e) => {
                const d = e.target.files?.[0] ?? null
                setDatei(d)
                if (d) void pruefen(d, zeichensatz, eigenes)
              }}
            />
          </label>
          {aktuelles && (
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={eigenes}
                onChange={(e) => {
                  setEigenes(e.target.checked)
                  if (datei) void pruefen(datei, zeichensatz, e.target.checked)
                }}
              />
              als eigenes LV (nicht als neue Version)
            </label>
          )}
          {(v?.format === 'B2063' || falscherZeichensatz) && (
            <label className="flex items-center gap-1 text-sm">
              Zeichensatz
              <select
                className={`${inlineInput} !w-36`}
                value={zeichensatz}
                onChange={(e) => {
                  const zs = e.target.value as LvZeichensatz | ''
                  setZeichensatz(zs)
                  if (datei) void pruefen(datei, zs, eigenes)
                }}
              >
                <option value="">erkannt ({v?.encoding})</option>
                <option value="cp850">CP850 (DOS)</option>
                <option value="latin1">ISO 8859-1</option>
                <option value="utf-8">UTF-8</option>
              </select>
            </label>
          )}
          {laeuft && <span className="text-sm text-muted-foreground">Lese …</span>}
        </div>

        {v && (
          <div className="mt-4 space-y-4">
            <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
              <div>
                <span className="text-muted-foreground">Format:</span> {v.format} {v.formatVersion} · {LV_PHASE[v.phase] ?? v.phase}
              </div>
              <div>
                <span className="text-muted-foreground">Bezeichnung:</span> {v.title}
              </div>
              {v.project && (
                <div>
                  <span className="text-muted-foreground">Vorhaben:</span> {v.project}
                </div>
              )}
              {v.client && (
                <div>
                  <span className="text-muted-foreground">Auftraggeber:</span> {v.client}
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Positionen:</span> {v.stats.items} in {v.stats.groups} Gruppen
              </div>
              <div>
                <span className="text-muted-foreground">Preise:</span> {v.hasPrices ? 'ja' : 'nein'}
              </div>
            </div>

            {v.comparison && (
              <div className="rounded-md border border-border p-3">
                <div className="mb-2 text-sm font-semibold">
                  Abgleich mit Version {aktuelles?.version}: {v.comparison.changed} geändert · {v.comparison.new} neu · {v.comparison.removed} entfallen · {v.comparison.unchanged} unverändert
                </div>
                <p className="mb-2 text-xs text-muted-foreground">
                  Das LV wird vollständig als neue Version übernommen. Verkaufszeilen bleiben, wie sie sind, und zeigen „LV geändert" — übernommen wird dort je Zeile.
                </p>
                <div className="max-h-48 overflow-auto text-sm">
                  {v.comparison.lines.map((l) => (
                    <div key={`${l.status}-${l.oz}`} className="flex gap-2 py-0.5">
                      <span className="w-24 shrink-0 tabular-nums text-muted-foreground">{l.oz}</span>
                      <span className={`w-20 shrink-0 text-xs ${l.status === 'REMOVED' ? 'text-destructive' : 'text-foreground'}`}>
                        {l.status === 'CHANGED' ? 'geändert' : l.status === 'NEW' ? 'neu' : 'entfallen'}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {l.shortText}
                        {l.changes.length > 0 && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {l.changes.map((c) => `${LV_FELD[c.field] ?? c.field}: ${wert(c.field, c.before)} → ${wert(c.field, c.after)}`).join(' · ')}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Gliederung</div>
                <div className="max-h-48 overflow-auto rounded-md border border-border p-2">
                  <Baum gruppen={v.groups} />
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Einheiten</div>
                <div className="max-h-48 overflow-auto rounded-md border border-border p-2 text-sm">
                  {v.units.map((u) => (
                    <div key={u.unit} className="flex justify-between gap-2">
                      <span>
                        {u.unit} <span className="text-xs text-muted-foreground">({u.count})</span>
                      </span>
                      {u.unitCode ? <span className="text-muted-foreground">→ {u.unitCode}</span> : <span className="text-destructive">ohne Übersetzung</span>}
                    </div>
                  ))}
                </div>
                {v.units.some((u) => !u.unitCode) && <p className="mt-1 text-xs text-muted-foreground">Übersetzt wird danach im Reiter LV.</p>}
              </div>
            </div>

            {v.notes.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                {v.notes.slice(0, 8).map((n, i) => (
                  <li key={i}>{n.text}</li>
                ))}
              </ul>
            )}

            {!alsVersion && (v.project || v.title) && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={kopf} onChange={(e) => setKopf(e.target.checked)} />
                Beschreibung des Projekts aus dem LV übernehmen, wenn leer
              </label>
            )}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50" disabled={!v || laeuft} onClick={() => void importieren()}>
            Importieren
          </button>
        </div>
      </div>
    </div>
  )
}
